import {
  Button,
  Flex,
  FormControl,
  Grid,
  Switch,
  Text
} from '@chakra-ui/react'
import { CustomCollapse, NumberFormControl, RadioGroupFormControl } from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { CalcMinAmountHouseholdRequest, InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Insurer, RiskVariant, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useState } from 'react'

import { CustomNumberInput, FormLabel, InputGroup, QuestionMarkIcon } from '@/components'
import { HorizontalRadios } from '@/components/HorizontalRadios'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useInsurers, useSelections } from '@/hooks'
import { useCalcRecommendedAmountHousehold } from '@/hooks/useWizard'
import { Calculate } from '@/icons'

export const HouseholdForm = () => {
  const { isDisabled } = useInsuranceParams()
  const { isInsurerAvailable } = useInsurers(false, null)
  const { data: dataTypKonstrukce } = useSelections(SelectionType.TypKonstrukce)
  const { data: dataStrechy } = useSelections(SelectionType.TypStrechy)
  const { data: dataOwner } = useSelections(SelectionType.Vlastnictvi)
  const { data: dataVybaveni } = useSelections(SelectionType.Vybaveni)

  const {
    setValue,
    register,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext<InsuranceParamsPropertyDto>()

  const selectedType = watch('property.type')
  const selectedRealtyType = watch('property.data.realtyTypeId')
  const householdRoofType = watch('property.data.household.roofType')

  const isOpenApartment = !!selectedRealtyType && (selectedRealtyType == Selection.Byt || selectedRealtyType == Selection.NajemniByt)
  const isOpenRealty = !!selectedType && selectedType != RiskVariant.Household
  const isOpenHouseholdRoofSedlova = householdRoofType == Selection.StrechaSedlova

  const [calcHouseholdAmountError, setCalcHouseholdAmountError] = useState('')
  const { mutateAsync: calcRecommendedHouseholdAmount } = useCalcRecommendedAmountHousehold()

  let livingAreaHint = 'Celková započitatelná plocha bytu je podlahová plocha bytu včetně kuchyně, chodeb, WC, koupelny a schodiště. Do této plochy se nezapočítávají plochy balkonů, lodžií, teras, plochy ostatních příslušenství bytu a nebytových prostor.'
  if (isInsurerAvailable(Insurer.KB) && !!selectedRealtyType && selectedRealtyType == Selection.RodinnyDum) {
    livingAreaHint += ' Pro pojišťovnu Komerční pojišťovna: V případě půdních prostor, započtěte pouze obytnou část.'
  }

  const calcHouseholdAmount = () => {
    const req: CalcMinAmountHouseholdRequest = {
      livingArea: getValues('property.data.household.livingArea'),
      equipment: getValues('property.data.household.equipment'),
    }
    calcRecommendedHouseholdAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.household.amount', resp.data.amount!)
        setCalcHouseholdAmountError('')
      } else {
        setCalcHouseholdAmountError(resp.data.message)
      }
    })
  }

  return (
    <Grid rowGap={12} w='100%'>
      <FormRow>
        {/* Podlahová plocha */}
        <NumberFormControl name='property.data.household.livingArea' isRequired
          label="Podlahová plocha"
          labelProps={{ hint: livingAreaHint }}
          defaultValue={getValues('property.data.household.livingArea')}
          inputProps={{
            placeholder: 'Např. 128'
          }}
          inputRightElement="㎡"
          w='100%'
          isDisabled={isDisabled}
        />

        {/* Poschodí */}
        <FormControl
          isInvalid={!!errors.property?.data?.household?.floor}
          isRequired
          isDisabled={isDisabled}
        >
          <FormLabel tooltip="Pro domácnost v přízemí, sklepním bytě nebo vyvýšeném přízemí uveďte hodnotu 0, jinak uveďte číslo patra. Pokud je domácnost ve více podlažích, uveďte to nejnižší. Hodnota může být vyžadována pojišťovnou pro určení rizika povodně.">
            Poschodí
          </FormLabel>

          <CustomNumberInput
            w='100%'
            defaultValue={getValues('property.data.household.floor')}
            onValueChange={(_, value) =>
              setValue('property.data.household.floor', value)
            }
            isDisabled={isDisabled}
            inputProps={{
              placeholder: 'Např. 128'
            }}
            inputRightElement=''
          />

          {(isInsurerAvailable(Insurer.Axa) ||
            isInsurerAvailable(Insurer.GeneraliCeskaPojistovna)) && (
            <CustomCollapse in={isOpenApartment}>
              <FormControl display='flex' alignItems='center' justifyContent='center'>
                <Switch
                  {...register('property.data.household.isApartmentOnLastFloor')}
                  isChecked={watch('property.data.household.isApartmentOnLastFloor')}
                  isDisabled={isDisabled}
                />

                <FormLabel tooltip="Zaškrtněte, pokud je byt v posledním poschodí bytového domu">
                  Poslední patro
                </FormLabel>
              </FormControl>
            </CustomCollapse>
          )}
        </FormControl>

        {/* Číslo bytu */}
        <FormControl>
          <FormLabel tooltip="Napište číslo bytové jednotky číslicí. Pro byt v rodinném domě uveďte číslici 1 nebo 2 nebo 3.">
            Číslo bytu
          </FormLabel>

          <InputGroup
            placeholder="Např. 1"
            {...register('property.data.household.apartmentNumber')}
            isDisabled={isDisabled}
          />
        </FormControl>

        {/* Počet osob v domácnosti */}
        <FormControl>
          <FormLabel tooltip="Zadejte počet osob v domácnosti">
            Počet osob v domácnosti
          </FormLabel>

          <InputGroup
            placeholder="Např. 5"
            {...register('property.data.household.countOfPeople')}
            isDisabled={isDisabled}
          />
        </FormControl>
      </FormRow>

      {isInsurerAvailable(Insurer.Pillow) && (
        <>
          <CustomCollapse in={!isOpenRealty}>
            <FormRow>
              {/* Rok kolaudace či výstavby */}
              <FormControl
                isInvalid={
                  !!errors.property?.data?.household?.approvedYear
                }
              >
                <FormLabel tooltip="Pokud je nemovitost zkolaudována, uveďte rok kolaudace. Pokud koladudace nebyla nutná uveďte rok výstavby. Údaj je povinný pro pojišťovnu Pillow">
                  Rok kolaudace či výstavby
                </FormLabel>

                <InputGroup
                  {...register('property.data.household.approvedYear')}
                  errors={
                    errors.property?.data?.household?.approvedYear
                  }
                  isDisabled={isDisabled}
                />
              </FormControl>
            </FormRow>
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty}>
            <FormRow>
              {/* Typ konstrukce */}
              <RadioGroupFormControl name='property.data.household.structuralType' valueAsNumber
                label='Typ konstrukce'
                isDisabled={isDisabled}
              >
                <HorizontalRadios options={dataTypKonstrukce?.data.map((item) => ({
                  value: item.id.toString(),
                  label: item.description
                }))} />
              </RadioGroupFormControl>
            </FormRow>
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty && !isOpenApartment}>
            <FormRow>
              {/* Počet nadzemních podlaží */}
              <FormControl isInvalid={!!errors.property?.data?.household?.floorsCount}                  >
                <FormLabel tooltip="Počet nadzemních podlaží">
                  Počet nadzemních podlaží
                </FormLabel>

                <InputGroup
                  placeholder="Např. 1"
                  {...register('property.data.household.floorsCount')}
                  errors={errors.property?.data?.household?.floorsCount}
                  isDisabled={isDisabled}
                />
              </FormControl>

              {/* Typ střechy */}
              <RadioGroupFormControl name='property.data.household.roofType' valueAsNumber
                label='Typ střechy'
                isDisabled={isDisabled}
              >
                <HorizontalRadios options={dataStrechy?.data.map((item) => ({
                  value: item.id.toString(),
                  label: item.description
                }))} />
              </RadioGroupFormControl>

              <CustomCollapse in={isOpenHouseholdRoofSedlova}>
                {/* Je podkroví obytné */}
                <RadioGroupFormControl name='property.data.household.isRoofResidentia' valueAsBoolean isRequired
                  label='Je podkroví obytné'
                  isDisabled={isDisabled}
                >
                  <HorizontalRadios options={[{ value: 'true', label: 'Ano' }, { value: 'false', label: 'Ne' }]} />
                </RadioGroupFormControl>
              </CustomCollapse>
            </FormRow>
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty && isOpenApartment}>
            {/* Vlastnictví */}
            <RadioGroupFormControl name='property.data.household.owner' valueAsNumber
              label='Vlastnictví'
              isDisabled={isDisabled}
            >
              <HorizontalRadios options={dataOwner?.data.map((item) => ({
                value: item.id.toString(),
                label: item.description
              }))} />
            </RadioGroupFormControl>
          </CustomCollapse>
        </>
      )}

      {/* Vybavení */}
      <RadioGroupFormControl name='property.data.household.equipment' valueAsNumber isRequired
        label='Vybavení'
        labelProps={{
          hint: <>
            Základní - domácnost je převážně vybavena základním
            zařízením pořízeným např. v bazaru a oblečení a obuv
            jsou nakupovány převážně v asijských tržnicích.
            <br />
            Standardní - domácnost je převážně vybavena standardním
            zařízením a oblečením a obuví, které jsou běžně dostupné
            na našem trhu (sériová výroba - např. IKEA, Sconto,
            Baťa, Hervis).
            <br />
            Luxusní - domácnost je převážně vybavena luxusním
            zařízením nebo zařízením pořízeným na zakázku, oblečení
            i obuv jsou převážně značkové."
          </>
        }}
        isDisabled={isDisabled}
      >
        <HorizontalRadios options={dataVybaveni?.data.map((item) => ({
          value: item.id.toString(),
          label: item.description
        }))} />
      </RadioGroupFormControl>

      <FormRow itemsCount={2} alignItems={'end'}>
        {/* Vybavení domácnosti */}
        <NumberFormControl name='property.data.household.amount' isRequired
          label="Vybavení domácnosti"
          labelProps={{ hint: 'Nábytek, elektronika a spotřebiče, koberce, oblečení, hračky, nádobí, sportovní potřeby, kosmetika…' }}
          defaultValue={getValues('property.data.household.amount')}
          inputRightElement="Kč"
          inputProps={{
            placeholder: 'Např. 10 000 000'
          }}
          w='100%'
          isDisabled={isDisabled}
        />

        <Flex sx={{ gap: 4, alignItems: 'center', pb: 3 }}>
          <Button
            variant="link"
            isDisabled={isDisabled}
            onClick={() => calcHouseholdAmount()}
          >
            <Calculate color="secondary.500" />
            Orientační poj. částka
          </Button>

          <CustomTooltip
            trigger="clickHover"
            hasArrow
            label='Jedná se o orientační pojistnou částku vypočítanou dle obecného vzorce, se započítaným nárůstem cen stavebních prací a materiálů, za kterou neneseme odpovědnost'
            placement="top"
          >
            <QuestionMarkIcon />
          </CustomTooltip>

          {calcHouseholdAmountError && (
            <Text variant={'error'}>{calcHouseholdAmountError}</Text>
          )}
        </Flex>
      </FormRow>
    </Grid>
  )
}